import { 
  HeartPulse, 
  Shield, 
  Landmark, 
  FlaskConical, 
  Zap, 
  FileSearch,
  ExternalLink
} from "lucide-react";

const industries = [
  {
    icon: HeartPulse,
    title: "Healthcare",
    subtitle: "HIPAA Compliance",
    description: "Govern AI agents handling PHI with pre-execution validation, human-in-the-loop escalation, and HIPAA-ready audit trails. Prevent unauthorized access to patient records.",
    benefits: ["PHI Access Control", "Care Coordination Governance", "Claims Processing Audit"],
    color: "emerald",
    link: "https://healthguardhq.tech",
    linkText: "HealthGuardhq",
    hasLink: true
  },
  {
    icon: Shield,
    title: "Insurance",
    subtitle: "NAIC Model Bulletin",
    description: "Enforce least-privilege entitlements for AI underwriting and claims agents. Ensure every automated decision traces to a human owner for regulatory accountability.",
    benefits: ["Underwriting Governance", "Claims Automation Control", "Policyholder Data Protection"],
    color: "blue",
    hasLink: false
  },
  {
    icon: Landmark,
    title: "Financial Services & AML",
    subtitle: "OCC Guidelines",
    description: "Real-time blocking of unauthorized transactions and data access. Deterministic audit logs for AML compliance and regulatory examinations.",
    benefits: ["Transaction Monitoring", "AML/KYC Enforcement", "Fraud Detection Governance"],
    color: "indigo",
    hasLink: false
  },
  {
    icon: FlaskConical,
    title: "Pharma & Life Sciences",
    subtitle: "21 CFR Part 11",
    description: "Govern AI agents in clinical trials and drug development with immutable audit trails. Validate every agent action against approved protocols before execution.",
    benefits: ["Clinical Trial Governance", "Drug Safety Monitoring", "Research Data Integrity"],
    color: "violet",
    hasLink: false
  },
  {
    icon: Zap,
    title: "Energy & Utilities",
    subtitle: "NERC CIP",
    description: "Protect critical infrastructure with zero-trust entitlement governance. Ensure AI agents operating grid systems act within strictly defined boundaries.",
    benefits: ["Grid Operations Control", "SCADA Access Governance", "Infrastructure Protection"],
    color: "amber",
    hasLink: false
  },
  {
    icon: FileSearch,
    title: "Auditing & Compliance",
    subtitle: "SOC 2 Type II",
    description: "Provide auditors with deterministic, immutable evidence of AI agent governance. Every entitlement decision mapped to compliance frameworks.",
    benefits: ["Audit Trail Generation", "Compliance Mapping", "Risk Assessment Documentation"],
    color: "cyan",
    hasLink: false
  }
];

const getColorClasses = (color) => {
  const colors = {
    emerald: {
      bg: "bg-emerald-500/10",
      border: "border-emerald-500/20",
      text: "text-emerald-400",
      gradient: "from-emerald-500 to-teal-600",
      hover: "hover:border-emerald-500/40",
      badge: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
    },
    blue: {
      bg: "bg-blue-500/10",
      border: "border-blue-500/20",
      text: "text-blue-400",
      gradient: "from-blue-500 to-cyan-600",
      hover: "hover:border-blue-500/40",
      badge: "bg-blue-500/10 text-blue-400 border-blue-500/30"
    },
    indigo: {
      bg: "bg-indigo-500/10",
      border: "border-indigo-500/20",
      text: "text-indigo-400",
      gradient: "from-indigo-500 to-violet-600",
      hover: "hover:border-indigo-500/40",
      badge: "bg-indigo-500/10 text-indigo-400 border-indigo-500/30"
    },
    violet: {
      bg: "bg-violet-500/10",
      border: "border-violet-500/20",
      text: "text-violet-400",
      gradient: "from-violet-500 to-purple-600",
      hover: "hover:border-violet-500/40",
      badge: "bg-violet-500/10 text-violet-400 border-violet-500/30"
    },
    amber: {
      bg: "bg-amber-500/10",
      border: "border-amber-500/20",
      text: "text-amber-400",
      gradient: "from-amber-500 to-orange-600",
      hover: "hover:border-amber-500/40",
      badge: "bg-amber-500/10 text-amber-400 border-amber-500/30"
    },
    cyan: {
      bg: "bg-cyan-500/10",
      border: "border-cyan-500/20",
      text: "text-cyan-400",
      gradient: "from-cyan-500 to-blue-600",
      hover: "hover:border-cyan-500/40",
      badge: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30"
    }
  };
  return colors[color] || colors.violet;
};

export const IndustryCards = () => {
  return (
    <section 
      id="industries" 
      className="py-20 sm:py-28 lg:py-36 px-6 lg:px-10 border-y border-slate-900"
      data-testid="industries-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 lg:mb-20 reveal">
          <span 
            className="text-[9px] sm:text-[10px] font-extrabold text-violet-500 uppercase tracking-[0.4em] sm:tracking-[0.6em] mb-4 block"
            data-testid="industries-label"
          >
            Industry Solutions
          </span>
          <h2 
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tighter mb-6"
            data-testid="industries-headline"
          >
            Built for Regulated Industries
          </h2>
          <p className="text-base lg:text-lg text-slate-400 max-w-3xl mx-auto leading-relaxed">
            ElixirID maps every entitlement decision to the compliance frameworks your auditors and regulators require. One engine, one ledger, every control.
          </p>
        </div>

        {/* Industry Cards Grid */}
        <div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          data-testid="industry-cards"
        >
          {industries.map((industry, index) => {
            const colors = getColorClasses(industry.color);
            
            return (
              <div 
                key={index}
                className={`group relative p-6 lg:p-8 rounded-2xl bg-slate-900/30 border border-slate-800 ${colors.hover} transition-all duration-500 hover:bg-slate-900/50 flex flex-col`}
                data-testid={`industry-card-${index}`}
              >
                {/* Icon & Header */}
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-12 h-12 lg:w-14 lg:h-14 rounded-xl bg-gradient-to-br ${colors.gradient} flex items-center justify-center shadow-lg flex-shrink-0`}>
                    <industry.icon className="w-6 h-6 lg:w-7 lg:h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg lg:text-xl font-bold text-white mb-1">{industry.title}</h3>
                    <span className={`text-xs font-semibold ${colors.text} uppercase tracking-wider`}>
                      {industry.subtitle}
                    </span>
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-slate-400 leading-relaxed mb-6 flex-grow">
                  {industry.description}
                </p>

                {/* Benefits */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {industry.benefits.map((benefit, i) => (
                    <span 
                      key={i}
                      className={`text-[10px] font-semibold px-2 py-1 rounded border ${colors.badge}`}
                    >
                      {benefit}
                    </span>
                  ))}
                </div>

                {/* Link (if applicable) */}
                {industry.hasLink && (
                  <a 
                    href={industry.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-2 text-sm font-semibold ${colors.text} hover:underline mt-auto pt-4 border-t border-slate-800/50`}
                    data-testid={`industry-link-${index}`}
                  >
                    Visit {industry.linkText}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
